import CancelIcon from '@mui/icons-material/Cancel';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    createFilterOptions,
    TextField,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { ContactModel } from 'shared/types/Contacts';
import { isEmail } from 'shared/utils/string';

import { SelectContactsSection } from '@/components';
import { ReachedOutMaximumTeamCapacity } from '@/components/ReachedOutMaximumTeamCapacity';
import { useOrganisationsContext } from '@/context/OrganisationsContext';
import { useFetchContactsFn } from '@/hooks';
import Logger from '@/services/logger';
import { OrganisationsAPI } from '@/services/networking/organisations';
import { Snackbar } from '@/services/toastNotifications';
import { t } from '@/services/translations/config';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';
import { TeamValidationSchema } from '@/validations/team';

import {
    Props,
    TeamInvitationFormValues,
    ContactEmail,
    ContactOption,
} from './types';

const filter = createFilterOptions<ContactOption>();

const ContactInvitationForm: React.FC<Props> = ({
    handleClose,
    handleSuccessInvitation,
    hasReachedSeatsLimit,
    takenSeats,
    seatsLimit,
    handleReachedSeatsLimit,
}) => {
    const { fetchMembers } = useOrganisationsContext();
    const { fetchContacts, fetchContactsAvatars } = useFetchContactsFn({});
    const [contacts, setContacts] = useState<ContactModel[]>([]);
    const [contactsAvatars, setContactsAvatars] = useState<
        Record<string, string | undefined>
    >({});

    const handleSubmit = useCallback(
        async (formValues: TeamInvitationFormValues) => {
            if (hasReachedSeatsLimit) {
                return;
            }

            const contactEmails = formValues.contactsEmails.map(
                (c) => c.inputValue,
            );
            const contactEntitiesEmails = formValues.contactsEntities.map(
                (c) => c.email,
            );

            const emails = [...contactEmails, ...contactEntitiesEmails].filter(
                Boolean,
            ) as string[];

            try {
                await OrganisationsAPI.sendEmailInvitations({ emails });
                await fetchMembers();

                handleSuccessInvitation();
            } catch (error) {
                Snackbar.showToastNotification({
                    message: t('App:Messages:something_went_wrong'),
                    options: {
                        variant: 'error',
                    },
                });
                Logger.logError(
                    `Error while sending team invitations: ${error}`,
                );
            }
        },
        [handleSuccessInvitation, fetchMembers, hasReachedSeatsLimit],
    );

    const form = useFormik<TeamInvitationFormValues>({
        enableReinitialize: true,
        initialValues: { email: '', contactsEntities: [], contactsEmails: [] },
        onSubmit: handleSubmit,
        validateOnChange: true,
        validateOnMount: true,
        validateOnBlur: true,
        validationSchema: TeamValidationSchema.inviteToTeamForm,
    });

    const handleSelectContacts = useCallback(
        (contacts: ContactModel[]) => {
            form.setFieldValue('contactsEntities', contacts);
        },
        [form],
    );

    const handleRemoveContact = useCallback(
        (contact: ContactModel) => {
            const previousContacts = form.values.contactsEntities;
            const newContacts = previousContacts?.filter(
                (prevContact) => prevContact.id !== contact.id,
            );

            form.setFieldValue('contactsEntities', newContacts);
        },
        [form],
    );

    const handleContactEmailRemove = useCallback(
        (newContactEmail: string) => {
            form.setFieldValue(
                'contactsEmails',
                form.values.contactsEmails.filter(
                    (member) => member.inputValue !== newContactEmail,
                ),
            );
        },
        [form],
    );

    useEffect(() => {
        fetchContacts().then(setContacts);
    }, [fetchContacts]);

    useEffect(() => {
        setContactsAvatars(fetchContactsAvatars(contacts));
    }, [fetchContactsAvatars, contacts]);

    const selectedExtraItems = form.values.contactsEmails;
    const selectedContacts = form.values.contactsEntities;

    const takenSeatsTotal =
        takenSeats + selectedContacts.length + selectedExtraItems.length;
    const willReachSeatsLimit = Boolean(
        seatsLimit && takenSeatsTotal >= seatsLimit,
    );

    useEffect(() => {
        handleReachedSeatsLimit?.(willReachSeatsLimit);
    }, [willReachSeatsLimit, handleReachedSeatsLimit]);

    return (
        <Box component="form" onSubmit={form.handleSubmit}>
            <Box className="flex flex-col gap-4">
                <Typography>
                    {t('TeamInvitationModal:labels:invite_by_email')}
                </Typography>

                <Autocomplete
                    id="email"
                    sx={{ maxWidth: 350 }}
                    onBlur={form.handleBlur}
                    disabled={willReachSeatsLimit}
                    clearIcon={
                        <CancelIcon
                            sx={{
                                width: 18,
                                height: 18,
                                color: COLOR.black,
                            }}
                        />
                    }
                    onChange={(_, newValue) => {
                        if (!newValue || typeof newValue === 'string') {
                            return;
                        }

                        if ('inputValue' in newValue) {
                            form.setFieldValue('contactsEmails', [
                                { inputValue: newValue.inputValue },
                                ...form.values.contactsEmails,
                            ]);
                            form.setFieldValue('email', '');
                            return;
                        }

                        const currentContacts = form.values.contactsEntities;
                        const contactAlreadyExists = currentContacts.some(
                            (c) => c.id === newValue.id,
                        );

                        if (!contactAlreadyExists) {
                            form.setFieldValue('contactsEntities', [
                                newValue,
                                ...currentContacts,
                            ]);
                        }

                        form.setFieldValue('email', '');
                    }}
                    value={null}
                    inputValue={form.values.email}
                    onInputChange={(_, newInputValue) => {
                        form.setFieldValue('email', newInputValue);
                    }}
                    freeSolo
                    selectOnFocus
                    options={contacts as ContactOption[]}
                    blurOnSelect
                    groupBy={() => t('TeamInvitationModal:labels:suggestions')}
                    renderGroup={(params) => (
                        <React.Fragment key={params.key}>
                            <Typography
                                fontWeight={700}
                                fontSize={12}
                                color={COLOR.ebonyClay}
                                sx={{ py: '12px', px: '24px' }}
                            >
                                {params.group}
                            </Typography>
                            {params.children}
                        </React.Fragment>
                    )}
                    slotProps={{
                        popper: {
                            sx: { mt: '12px!important' },
                        },
                    }}
                    filterOptions={(options, params) => {
                        const selectedContactEntitiesEmails =
                            form.values.contactsEntities.map((c) => c.email);
                        const selectedContactsEmails =
                            form.values.contactsEmails.map(
                                (member) => member.inputValue,
                            );

                        const filtered = filter(options, params).filter(
                            (c: ContactModel) =>
                                c.email &&
                                !selectedContactEntitiesEmails.includes(
                                    c.email,
                                ),
                        );

                        const newValue = params.inputValue.trim();

                        const isAlreadySelectedContact =
                            selectedContactEntitiesEmails.includes(newValue);

                        const isAlreadySelectedMember =
                            selectedContactsEmails.includes(newValue);

                        if (
                            !filtered.length &&
                            !isAlreadySelectedContact &&
                            !isAlreadySelectedMember &&
                            isEmail(newValue)
                        ) {
                            filtered.push({
                                inputValue: newValue,
                            });
                        }

                        return filtered;
                    }}
                    getOptionLabel={(option) => {
                        if (typeof option === 'string') {
                            return option;
                        }
                        if ('inputValue' in option) {
                            return option.inputValue;
                        }
                        return option.email || '';
                    }}
                    renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        const isNewContactEmail = 'inputValue' in option;
                        return (
                            <Box
                                key={key}
                                component="li"
                                className="flex items-center"
                                {...optionProps}
                            >
                                <Avatar
                                    src={
                                        !isNewContactEmail
                                            ? contactsAvatars[option.id]
                                            : undefined
                                    }
                                    className="mr-3 w-12 h-12"
                                />
                                <Box className="flex flex-col gap-0.5">
                                    <Typography fontSize={14} fontWeight={700}>
                                        {isNewContactEmail
                                            ? option.inputValue
                                            : [
                                                  option.firstName,
                                                  option.lastName,
                                              ]
                                                  .filter(Boolean)
                                                  .join(' ')}
                                    </Typography>
                                    <Typography
                                        color={COLOR.dustyGray}
                                        fontSize={10}
                                    >
                                        {isNewContactEmail
                                            ? t(
                                                  'Organisation:invite_via_email:not_in_contact',
                                              )
                                            : option.email}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            name="email"
                            label={t(
                                'TeamInvitationModal:labels:email_address',
                            )}
                        />
                    )}
                />

                <SelectContactsSection
                    title="Team Member Invitations"
                    onSelectContacts={handleSelectContacts}
                    onRemoveContact={handleRemoveContact}
                    selectedContacts={form.values.contactsEntities}
                    testIdPrefix="TeamInvitationModal"
                    selectCondition={(contact) => !!contact.email}
                    conditionWarning={t('Contact:alert:no_email_provided')}
                    displayContactInformation={false}
                    allowCreate={false}
                    selectLimit={seatsLimit}
                    externalSelectedItemsCount={
                        takenSeats + selectedExtraItems.length
                    }
                    extraItems={selectedExtraItems}
                    renderExtraItemComponent={(item: ContactEmail) => (
                        <Box
                            className="flex rounded-l-[37px] min-h-[74px]"
                            sx={{
                                backgroundColor: COLOR.athensGrayDarker,
                            }}
                            key={item.inputValue}
                        >
                            <Avatar className="h-[74px] w-[74px] mr-4" />

                            <Box className="flex gap-2 justify-between items-center py-1 pr-2 w-full">
                                <Box className="max-w-[190px]">
                                    <Typography
                                        className="break-all"
                                        fontSize={14}
                                        fontWeight={700}
                                    >
                                        {item.inputValue}
                                    </Typography>
                                    <Typography
                                        color={COLOR.dustyGray}
                                        fontSize={10}
                                    >
                                        {t(
                                            'Organisation:invite_via_email:not_in_contact',
                                        )}
                                    </Typography>
                                </Box>

                                <Button
                                    className="px-0 min-w-0"
                                    onClick={() =>
                                        handleContactEmailRemove(
                                            item.inputValue,
                                        )
                                    }
                                    size="small"
                                >
                                    <DeleteOutlineRoundedIcon />
                                </Button>
                            </Box>
                        </Box>
                    )}
                    renderWholeContentComponent={(item: ContactModel) => (
                        <Box>
                            <Typography fontSize={14} fontWeight={700}>
                                {item.firstName} {item.lastName}
                            </Typography>
                            <Typography color={COLOR.dustyGray} fontSize={10}>
                                {item.email}
                            </Typography>
                        </Box>
                    )}
                    ReachedOutMaximumCapacityNode={
                        <ReachedOutMaximumTeamCapacity />
                    }
                />
            </Box>

            <Box className="flex gap-4 justify-end mt-5">
                <Button onClick={handleClose}>
                    <Typography
                        sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: COLOR.tundora,
                        }}
                    >
                        {t('Actions:cancel')}
                    </Typography>
                </Button>

                <Button
                    type="submit"
                    className="px-8 py-2.5"
                    variant="contained"
                    disabled={
                        hasReachedSeatsLimit ||
                        (!form.values.contactsEntities.length &&
                            !form.values.contactsEmails.length) ||
                        form.isSubmitting
                    }
                    sx={{
                        backgroundColor: ENTITY_COLOR.teams,
                        '&:hover': {
                            backgroundColor: ENTITY_COLOR.teams,
                        },
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: COLOR.white,
                        }}
                    >
                        {t('Actions:send')}
                    </Typography>
                </Button>
            </Box>
        </Box>
    );
};

export default ContactInvitationForm;
